import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, map } from 'lodash';
import { JsonLdScript } from 'components/atoms';
import Crumb from './Crumb';

import './Breadcrumbs.scss';

function buildHref(rawHref, rootUrl) {
	if (!rawHref) return rootUrl;
	if (/^https?:\/\//i.test(rawHref)) return rawHref;
	const path = rawHref.startsWith('/') ? rawHref : `/${rawHref}`;
	return `${rootUrl.replace(/\/$/, '')}${path}`;
}

function createCLPBreadcrumbSchema(
	baseUrl = 'https://www.selfstorage.com',
	crumbs = [],
) {
	const ROOT_URL = baseUrl;
	const ensureTrailingSlash = (url) => {
		if (!url) return url;
		return url.endsWith('/') ? url : `${url}/`;
	};
	const filtered = crumbs.filter((crumb) => crumb?.props?.label);

	const itemListElement = filtered.map((crumb, index) => {
		const rawHref = (get(crumb, 'props.href', '') || '').split('?').shift();
		const absoluteUrl = buildHref(rawHref, ROOT_URL);
		const idUrl = ensureTrailingSlash(absoluteUrl);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@id': idUrl,
				name: crumb.props.name || crumb.props.label,
				url: idUrl,
			},
		};
	});

	const cityCrumb = filtered.find(
		(c) => c?.props?.className === 'sublocality',
	);
	let landingUrl = '';

	if (cityCrumb) {
		const raw = (get(cityCrumb, 'props.href', '') || '').split('?').shift();
		landingUrl = ensureTrailingSlash(buildHref(raw, ROOT_URL));
	} else {
		landingUrl =
			get(
				itemListElement,
				`[${itemListElement.length - 1}].item['@id']`,
				'',
			) || ensureTrailingSlash(ROOT_URL);
	}

	const breadcrumbListId = `${landingUrl.split('#')[0]}#breadcrumb`;
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		'@id': breadcrumbListId,
		itemListElement,
	};
}

function createCrumbSchema(crumbs) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': get(crumb, 'props.id', get(crumb, 'props.to', ''))
						.split('?')
						.shift(),
					name: crumb.props.name || crumb.props.label,
				},
			})),
	};
}

export function Breadcrumbs({
	location = {},
	crumbs: crumbsProp = [],
	cityUrl,
	stateUrl,
	baseUrl,
	tertiary,
	isCLPPage,
	className,
	suppressSchema,
}) {
	const { crumbs, crumbJSON } = useMemo(() => {
		const newCrumbs = [];
		const { fullState, subLocality, city } = location;
		const sub = subLocality || city;

		newCrumbs.push(
			<Crumb
				href={`${baseUrl}`}
				id="/self-storage/"
				key="base"
				label="Home"
				name="Home"
				baseUrl={baseUrl}
				tertiary={tertiary}
			/>,
		);

		if (stateUrl) {
			newCrumbs.push(
				<Crumb
					key="state"
					href={baseUrl + stateUrl}
					isLast={!cityUrl && !crumbsProp.length}
					label={fullState}
					name={fullState}
					className="state"
					tertiary={tertiary}
				/>,
			);
		}

		if (cityUrl) {
			const isLast = !crumbsProp.length;
			newCrumbs.push(
				<Crumb
					key="sublocality"
					className="sublocality"
					href={baseUrl + cityUrl}
					isLast={isLast}
					label={`${sub} Storage`}
					name={sub}
				>
					{sub}
				</Crumb>,
			);
		}

		const allCrumbs = [...newCrumbs, ...crumbsProp];
		return {
			crumbs: allCrumbs,
			crumbJSON: isCLPPage
				? createCLPBreadcrumbSchema(baseUrl, allCrumbs)
				: createCrumbSchema(allCrumbs),
		};
	}, [location, crumbsProp, cityUrl, stateUrl, baseUrl, tertiary, isCLPPage]);

	return (
		<Fragment>
			<ul
				className={classnames('ss-breadcrumbs', 'core', className)}
				id="breadcrumb"
			>
				{map(crumbs, (crumb) => (
					<li
						className="ss-breadcrumb"
						key={crumb.key}
					>
						{crumb}
					</li>
				))}
			</ul>
			{!suppressSchema && <JsonLdScript schema={crumbJSON} />}
		</Fragment>
	);
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	crumbs: PropTypes.node,
	location: PropTypes.shape({
		fullState: PropTypes.string,
		subLocality: PropTypes.string,
		city: PropTypes.string,
	}),
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	baseUrl: PropTypes.string,
	tertiary: PropTypes.bool,
	isCLPPage: PropTypes.bool,
	suppressSchema: PropTypes.bool,
};

export default Breadcrumbs;
